import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  initializeAuth,
  checkSubscription as checkSubscriptionThunk,
  login as loginAction,
  logout as logoutAction,
} from '../store/authSlice';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  loading: boolean;
  hasActiveSubscription: boolean;
  userRole: string | null;
  checkSubscription: () => Promise<void>;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const { token, userRole, isAuthenticated, hasActiveSubscription, loading } =
    useAppSelector((state) => state.auth);

  // Initialize auth from localStorage on mount
  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  // Check subscription status when token/role changes
  useEffect(() => {
    if (token && userRole === 'Client') {
      dispatch(checkSubscriptionThunk());
    }
  }, [token, userRole, dispatch]);

  const checkSubscription = async () => {
    await dispatch(checkSubscriptionThunk()).unwrap();
  };

  const login = (newToken: string) => {
    dispatch(loginAction(newToken));
  };

  const logout = () => {
    dispatch(logoutAction());
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        loading,
        hasActiveSubscription,
        userRole,
        checkSubscription,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
